import { Suspense, createContext, lazy, useContext, useEffect, useState } from 'react';
import { Route, Routes } from 'react-router';
import { api, ApiFehler, ABGEMELDET, anmeldenUrl, sitzung, type Ich } from './api';
import { getState } from './logik/store';
import { Start } from './ui/Start';
import { Projekt } from './ui/Projekt';
import { Landingpage } from './ui/Landingpage';
import { bindToast, toast } from './ui/toast';
import { FragenHost } from './ui/frage';
import { jetztSpeichern, meldungen } from './sync';
import { neueVersion } from './pwa/registrieren';

const HilfeSeite = lazy(() => import('./ui/Hilfe').then((m) => ({ default: m.HilfeSeite })));

/** Angemeldete Person samt sichtbarer Kunden und Projekte; neu laden nach Änderungen. */
export const IchKontext = createContext<{ ich: Ich | null; neuLaden: () => void }>({ ich: null, neuLaden: () => {} });
export const useIch = () => useContext(IchKontext);

interface Toast { msg: string; err: boolean; t: number }

export function App() {
  const [ich, setIch] = useState<Ich | null>(null);
  const [fehler, setFehler] = useState<{ text: string; status: number; keinZugang?: boolean } | null>(null);
  const abgemeldet = location.pathname === ABGEMELDET;
  // Startseite ohne Anmeldung: Landingpage statt sofortiger Weiterleitung zu Microsoft (Deep-Links leiten weiter direkt um)
  const [oeffentlich, setOeffentlich] = useState(false);
  const [toastMsg, setToast] = useState<Toast | null>(null);

  const [abgelaufen, setAbgelaufen] = useState('');
  const neuLaden = () => api.ich()
    .then((i) => { setIch(i); sitzung.aktiv = true; })
    .catch((e) => {
      if (sitzung.aktiv) return;
      const f = e instanceof ApiFehler ? e : null;
      setFehler({ text: e.message, status: f?.status ?? 0, keinZugang: (f?.daten as { keinZugang?: boolean } | undefined)?.keinZugang === true });
    });
  // Grund (Anfrage und Meldung der API) nur in der Konsole, nicht im Dialog
  useEffect(() => { sitzung.abgelaufen = (grund) => { console.info('Anmeldung abgelaufen:', grund); setAbgelaufen((a) => a || grund); }; }, []);
  useEffect(() => {
    if (abgemeldet) return;
    if (location.pathname !== '/') { void neuLaden(); return; }
    // /.auth/me gibt es nur bei Static Web Apps; fehlt es (lokal, Tests), lädt die App wie bisher
    fetch('/.auth/me', { credentials: 'same-origin' })
      .then((r) => (r.ok ? r.json() : null))
      .then((m) => { if (m && typeof m === 'object' && 'clientPrincipal' in m && !m.clientPrincipal) setOeffentlich(true); else void neuLaden(); })
      .catch(() => void neuLaden());
  }, []);
  useEffect(() => {
    bindToast(setToast);
    meldungen.zeigen = (msg, err = false) => setToast({ msg, err, t: Date.now() });
  }, []);
  useEffect(() => {
    if (!toastMsg) return;
    const t = setTimeout(() => setToast(null), toastMsg.err ? 7000 : 3200);
    return () => clearTimeout(t);
  }, [toastMsg]);

  // Hinweis auf eine neue Version auch auf Anmelde-, Fehler- und Ladeseite
  if (abgemeldet) return <><Landingpage hinweis="Sie sind abgemeldet." ziel="/" /><NeueVersion /></>;
  if (oeffentlich) return <><Landingpage ziel="/" /><NeueVersion /></>;
  if (fehler?.keinZugang) return <><Landingpage keinZugang={fehler.text} /><NeueVersion /></>;
  if (fehler?.status === 401) return <><Landingpage hinweis="Bitte melden Sie sich an." /><NeueVersion /></>;
  if (fehler) return <><div className="empty-state" data-testid="fehler">Projects ist gerade nicht erreichbar: {fehler.text}</div><NeueVersion /></>;
  if (!ich) return <><div className="empty-state">Wird geladen …</div><NeueVersion /></>;

  return (
    <IchKontext.Provider value={{ ich, neuLaden }}>
      <Routes>
        <Route path="/" element={<Start />} />
        <Route path="/p/:projektId/*" element={<Projekt />} />
        <Route path="/hilfe/:thema?" element={<Suspense fallback={<div className="empty-state">Hilfe wird geladen …</div>}><HilfeSeite /></Suspense>} />
        <Route path="*" element={<Start />} />
      </Routes>
      <FragenHost />
      <NeueVersion />
      {toastMsg && <div className={'toast' + (toastMsg.err ? ' err' : '')} role="status">{toastMsg.msg}</div>}
      {abgelaufen && (
        <div className="overlay" role="presentation">
          <div className="dialog" role="dialog" aria-modal="true" aria-label="Anmeldung abgelaufen" data-testid="sitzung-abgelaufen" style={{ maxWidth: 480 }}>
            <h2>Anmeldung abgelaufen</h2>
            <p>Ihre Anmeldung ist nicht mehr gültig. Bitte melden Sie sich neu an – danach geht es auf dieser Seite weiter.</p>
            {getState().dirty && <p className="warnung">Es gibt noch nicht gespeicherte Änderungen; sie gehen beim Neu-Anmelden verloren.</p>}
            <div className="knoepfe">
              <button className="btn" onClick={() => setAbgelaufen('')}>Später</button>
              <a className="btn primary" href={anmeldenUrl()} style={{ textDecoration: 'none' }} data-testid="neu-anmelden">Neu anmelden</a>
            </div>
          </div>
        </div>
      )}
    </IchKontext.Provider>
  );
}

/** Hinweis unten, sobald eine neue Version übernommen hat; „Aktualisieren“ speichert erst und lädt dann neu. */
function NeueVersion() {
  const [da, setDa] = useState(neueVersion.da());
  const [laeuft, setLaeuft] = useState(false);
  useEffect(() => neueVersion.beobachten(() => setDa(true)), []);
  if (!da) return null;
  const aktualisieren = async () => {
    setLaeuft(true);
    if (getState().dirty) await jetztSpeichern();
    if (getState().dirty) {
      setLaeuft(false);
      toast('Die Änderungen konnten nicht gespeichert werden – bitte erst speichern, dann aktualisieren.', true);
      return;
    }
    location.reload();
  };
  return (
    <div className="neue-version" role="status" data-testid="neue-version">
      <span>Neue Version verfügbar</span>
      <button className="btn small primary" disabled={laeuft} onClick={aktualisieren} data-testid="neue-version-aktualisieren">
        {laeuft ? 'Speichert …' : 'Aktualisieren'}</button>
    </div>
  );
}
